import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useItemInformationQuery } from 'shared/hooks/catalog/useItemInformationQuery';
import { Combobox } from 'shadcn/components/ui/Combobox';
import { useDebouncedValue } from 'shared';

import {
    DEFAULT_MAX_OPTIONS_RENDERED,
    filterAndLimitOptions,
    groupItemInfoNamesByExternalSku,
    normalizeSearchText,
} from '../utils/itemLookupUtils';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

export function ExternalSkuPicker({
    debounceMs = 150,
    enabled = true,
    label = 'External SKU',
    maxOptionsRendered = DEFAULT_MAX_OPTIONS_RENDERED,
    onValueChange,
    showLabel = true,
    value = '',
}) {
    const labelId = useId();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebouncedValue(searchTerm, debounceMs);

    const prevValueRef = useRef(String(value ?? ''));
    useEffect(() => {
        const nextValue = String(value ?? '');
        const prevValue = prevValueRef.current;
        prevValueRef.current = nextValue;
        if (!nextValue && prevValue && searchTerm) {
            setSearchTerm('');
        }
    }, [searchTerm, value]);

    const {
        data: itemInformation = [],
        isFetching,
        isLoading,
    } = useItemInformationQuery({
        activeOnly: true,
        enabled,
        itemType: 'itemMaster',
    });

    const baseOptions = useMemo(() => {
        const grouped = groupItemInfoNamesByExternalSku(itemInformation);

        const options = Array.from(grouped.entries()).map(([externalSku, namesSet]) => {
            const names = Array.from(namesSet).filter(Boolean);
            names.sort((a, b) => a.localeCompare(b));
            const primaryName = names[0] ?? '';
            const extraCount = Math.max(0, names.length - 1);
            const displayLabel =
                extraCount > 0
                    ? `SKU ${externalSku}: ${primaryName} (and ${extraCount} more)`
                    : `SKU ${externalSku}: ${primaryName}`;
            const searchText = normalizeSearchText(`${externalSku} ${names.join(' ')}`);
            return { label: displayLabel, searchText, value: externalSku };
        });

        options.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
        return options;
    }, [itemInformation]);

    const options = useMemo(() => {
        const selectedValue = String(value ?? '');
        const selectedOption =
            selectedValue && baseOptions.length ? baseOptions.find((option) => option.value === selectedValue) : null;
        const fallbackSelectedOption =
            selectedValue && !selectedOption ? { label: `SKU ${selectedValue}`, value: selectedValue } : null;
        const limitedOptions = filterAndLimitOptions(baseOptions, debouncedSearch, maxOptionsRendered).map(
            ({ label: optLabel, value: optValue }) => ({
                label: optLabel,
                value: optValue,
            }),
        );
        if (!selectedValue) return limitedOptions;
        if (limitedOptions.some((option) => option.value === selectedValue)) {
            return limitedOptions;
        }
        if (selectedOption) {
            return [selectedOption, ...limitedOptions];
        }
        if (fallbackSelectedOption) {
            return [fallbackSelectedOption, ...limitedOptions];
        }
        return limitedOptions;
    }, [baseOptions, debouncedSearch, maxOptionsRendered, value]);

    const showLoading = isLoading || (isFetching && baseOptions.length === 0);

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span id={labelId} className={labelClassName}>
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading external skus…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span id={labelId} className={labelClassName}>
                    {label}
                </span>
            )}
            <Combobox
                id={labelId}
                options={options}
                value={String(value ?? '')}
                handleSelect={(nextValue) => onValueChange(String(nextValue ?? ''))}
                placeholder="Select external sku"
                disabled={!options.length}
                popoverWidth="trigger"
                className="w-full"
                triggerClassName="w-full"
                truncateLabel
                onSearchValueChange={setSearchTerm}
                commandProps={{
                    // We filter/limit options ourselves; ensure cmdk doesn't hide items unexpectedly.
                    filter: () => 1,
                }}
            />
        </div>
    );
}

ExternalSkuPicker.displayName = 'ExternalSkuPicker';
